/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Thread analysis dashboard with pie chart of runtime distribution
 *         across scheduler threads and job runs table with time range filtering
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetDataParam} from '../../../../../core/shared/widget/widget-data-param';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {MutableAreaFilterChangeEvent} from '../../../../../core/shared/mutablearea/mutable-area-filter-change-event';
import {QlQueryDto} from '../../../../../core/shared/ql/data/dto/ql-query.dto';
import {QlWidgetFactory} from '../../../../../core/shared/widget/service/ql-widget.factory';

@Component({
    selector: 'jb-thread-analysis',
    templateUrl: 'jb-thread-analysis.component.html',
    styleUrls: ['jb-thread-analysis.component.scss'],
    standalone: false
})
export class JbThreadAnalysisComponent implements OnInit, OnChanges, OnDestroy {

    initialized: boolean;

    chartWidget: WidgetData;
    tableWidget: WidgetData;

    filterCriteria: FilterCriteria[] = [];
    selectedThread: number | null = null;

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.refreshWidgets();
        this.initialized = true;
    }

    /**
     * Handle filter change from mvs-range-selector-overlay.
     */
    handleFilterCriteria(filter: FilterCriteria): void {
        const index = this.filterCriteria.findIndex(criteria => criteria.field === 'e.startTime');
        if (index !== -1) {
            this.filterCriteria.splice(index, 1);
        }
        this.filterCriteria.push(filter);
        this.selectedThread = null;
        this.refreshWidgets();
    }

    /**
     * Handle clear from mvs-range-selector-overlay.
     */
    handleClearFilter(): void {
        this.filterCriteria = this.filterCriteria.filter(criteria => criteria.field !== 'e.startTime');
        this.selectedThread = null;
        this.refreshWidgets();
    }

    /**
     * Refresh both the chart and table widgets.
     */
    refreshWidgets(): void {
        this.refreshChartWidget();
        this.refreshTableWidget();
    }

    /**
     * Refresh the pie chart widget with QL groupBy on schedulerThread, SUM of runtimeInSeconds.
     */
    refreshChartWidget(): void {
        const qlQuery: QlQueryDto = {
            name: 'jb.threadAnalysis.runtimeDistribution.chart',
            type: 'view',
            start: {
                type: 'groupBy',
                name: 'jb.JbJobRun',
                as: 'e',
                fields: [
                    {name: 'schedulerThread'},
                    {name: 'runtimeInSeconds', function: 'sum', as: 'runtimeSum'}
                ]
            },
            fixedFilters: [...this.filterCriteria]
        };

        this.chartWidget = WidgetFactory.createWidgetList(
            'jb.threadAnalysis.runtimeDistribution.chart',
            'Laufzeitverteilung nach Threads',
            'chart',
            'list',
            'ql',
            '',
            'Keine Daten vorhanden',
            QlWidgetFactory.createWidgetRequest(qlQuery, 'resolved'),
            WidgetDataParam.create('chartType', 'pie'),
            WidgetDataParam.create('chartField1st', 'e.schedulerThread'),
            WidgetDataParam.create('chartField1stLabel', 'e.schedulerThreadEnumName'),
            WidgetDataParam.create('chartFieldAggregate', 'e.runtimeSum'),
            WidgetDataParam.create('chartAggregateFunction', 'sum'),
            WidgetDataParam.create('size', 'S')
        );
    }

    /**
     * Refresh the table widget with time range filters and optional thread filter.
     */
    refreshTableWidget(): void {
        const filters = [...this.filterCriteria];

        if (this.selectedThread !== null) {
            filters.push(
                FilterCriteria.create('e.schedulerThread', FilterCriteria.cOperatorEqual, this.selectedThread)
            );
        }

        this.tableWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'jb.threadAnalysis.jobRuns.table',
            'Job-Läufe',
            'jb.JbJobRun',
            filters,
            [new Sorting('e.startTime', false)]
        );
    }

    /**
     * Handle filter change from the chart widget (pie segment click).
     * Implements toggle behavior: clicking the same thread deselects it.
     */
    handleChartFilterChange(event: MutableAreaFilterChangeEvent): void {
        if (!event?.filterCriteriaCollection) {
            return;
        }

        const rootCriteria = event.filterCriteriaCollection.getRoot();
        if (!rootCriteria) {
            this.selectedThread = null;
            this.refreshTableWidget();
            return;
        }

        const clickedThread = rootCriteria.value;

        // Toggle behavior: if same thread clicked again, deselect
        if (this.selectedThread !== null && this.selectedThread === clickedThread) {
            this.selectedThread = null;
        } else {
            this.selectedThread = clickedThread;
        }

        this.refreshTableWidget();
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
    }
}
